<template>
  <div
    class="container v-content-container tooltip-wrapper"
    :key="componentKey"
  >
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-home"></i
          >{{
            `${$label("baubau", "Operator")}${
              noDataExist ? "" : ": " + operatorName
            }`
          }}
          <router-link
            class="btn btn-v-lightgreen-white pull-right"
            :to="{
              name: 'OperatorGeneralSettings',
              params: { id: selectedOperatorId }
            }"
            v-tooltip="{
              content: 'Neuen Betreiber anlegen',
              classes: 'custom-tooltip'
            }"
          >
            <i class="fa fa-fw fa-lg fa-plus"></i>
          </router-link>
          <select
            v-model="selectedOperatorId"
            class="form-control course-select pull-right"
            style="margin-right: 15px;"
          >
            <option
              v-for="(operator, index) in operators"
              :value="
                selectedOperatorId === 'default' && index === 0
                  ? 'default'
                  : operator.id
              "
              v-bind:key="operator.id"
              >{{ operator.name }}</option
            >
          </select>
        </h1>
      </div>
    </div>

    <navigation-tabs :operator-id="selectedOperatorId"></navigation-tabs>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <alert-box
        v-show="failedRequest && !loading"
        type="warning"
        :closeable="false"
        :show-icon="false"
      >
        {{
          $label(
            "operator.failed_request",
            "Request failed while loading operators."
          )
        }}
      </alert-box>

      <Table
        v-show="!failedRequest"
        id="additional-operators-table"
        :loading="loading"
        :columns="[
          {
            title: 'Name',
            field: 'name',
            id: row => `operator-name-${row.id}`
          },
          {
            title: 'Email',
            field: 'email',
            id: row => `operator-email-${row.id}`
          }
        ]"
        :actions="[
          {
            type: 'detail',
            onClick: row =>
              $router.push({
                name: 'OperatorGeneralSettings',
                params: { id: row.id }
              })
          },
          {
            type: 'delete',
            disabled: row => row.is_default,
            id: row => `delete-operator-button-${row.id}`,
            onClick: row => deleteOperator(row.id)
          }
        ]"
        :data="operators"
        :meta="metaData"
        @fetchQuery="
          params => {
            fetchParams = params;
            getOperators({
              //
            });
          }
        "
      >
        <div
          id="action-dropdown"
          slot="toolbar"
          class="btn-group"
          style="margin-left: 5px;"
        >
          <button
            type="button"
            class="btn btn-primary dropdown-toggle"
            data-toggle="dropdown"
          >
            <i class="fa fa-refresh"></i> &nbsp;
            {{ $label("common.actions", "Actions") }} &nbsp;<span
              class="caret"
            ></span>
          </button>
          <ul class="dropdown-menu" role="menu">
            <li>
              <router-link
                :to="{
                  name: 'OperatorGeneralSettings',
                  params: { id: selectedOperatorId }
                }"
                ><i class="text-primary fa fa-fw fa-lg fa-plus"></i>
                {{
                  $label("operator.create_new_operator", "Create new operator")
                }}</router-link
              >
            </li>
          </ul>
        </div>
      </Table>

      <div class="row mb-8">
        <div class="col-xs-12">&nbsp;</div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api.js";
import swal from "sweetalert";
import FormErrorsMixin from "@/mixins/FormErrors";

import NavigationTabs from "@/components/Settings/Operator/NavigationTabs.vue";
import Table from "@/components/Common/Table";
import AlertBox from "@/components/Common/Alerts/AlertBox";
import Error404 from "@/components/Common/Error404";

export default {
  mixins: [FormErrorsMixin],

  components: {
    Table,
    NavigationTabs,
    AlertBox,
    Error404
  },

  data() {
    return {
      operators: [],
      loading: false,
      updating: false,
      noDataExist: false,
      selectedOperator: null,
      selectedOperatorId: null,
      failedRequest: false,
      metaData: null,
      fetchParams: {
        page: 1,
        per_page: null,
        to: 1,
        search: ""
      },
      componentKey: 0
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedOperatorId = vm.$route.params.id;
      vm.getOperators(() => {
        vm.getOperator(() => {
          //
        });
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (to.query.operator_deleted === true) {
      swal("Success!", "The operator was deleted.", "success").then(() => {
        window.history.pushState({}, document.title, window.location.pathname);
      });
    }
    this.forceRerender();
    if (
      this.fetchParams.to % this.fetchParams.per_page === 0 &&
      this.fetchParams.page !== 1
    ) {
      this.fetchParams.page -= 1;
    }
    this.getOperators(() => {
      this.getOperator(() => {
        //
      });
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    getOperators(successCallback) {
      const endpoint = this.$partnerEndpoint("operators");

      Api()
        .get(endpoint, {
          params: {
            page: this.fetchParams.page,
            search: this.fetchParams.search
          }
        })
        .then(response => {
          this.operators = response.data.data;
          this.metaData = response.data.meta;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    createOperator() {
      const endpoint = this.$partnerEndpoint("operators");

      Api()
        .post(endpoint)
        .then(response => {
          this.selectedOperatorId = response.data.data.id;
        })
        .catch(error => {
          console.log(error);
        });
    },

    getOperator(successCallback) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "operators/" + this.selectedOperatorId
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedOperator = response.data.data;

          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    deleteOperator(operatorId) {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The operator will be deleted",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint("operators/" + operatorId);

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router.push({
                name: "AdditionalOperators",
                query: {
                  operator_deleted: true
                }
              });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete operators.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    forceRerender() {
      this.componentKey += 1;
    }
  },

  computed: {
    operatorName() {
      if (typeof this.operators == "undefined") {
        return "";
      }

      if (this.selectedOperatorId === "default" && this.selectedOperator) {
        return this.selectedOperator.name;
      }

      for (var i = 0; i < this.operators.length; i++) {
        if (this.operators[i].id == this.selectedOperatorId) {
          return this.operators[i].name;
        }
      }

      return "";
    }
  }
};
</script>

<style scoped>
.mt-0 {
  margin-top: 0;
}

.mt-8 {
  margin-top: 1.3rem;
}

.mb-0 {
  margin-bottom: 0;
}

.mb-1 {
  margin-bottom: 0.3rem;
}

.mb-2 {
  margin-bottom: 0.6rem;
}

.mb-8 {
  margin-bottom: 1.3rem;
}

.min-width-auto {
  width: 50px;
}

.pagination-select {
  font-weight: 700;
  height: 34px;
  padding-left: 10px;
  width: 70px;
  cursor: pointer;
  border: 1px solid #ddd;
  color: #666;
  outline: none;
}

@media screen and (max-width: 600px) {
}
</style>
